import React, {useContext, useState} from "react";
import {Modal} from "./Modal";
import {t} from "i18next";
import {AccountContext} from "@/context/AccountProvider";
import {NodeContext} from "@/context/NodeProvider";
import {addAdmin, removeAdmin, updateAdminRole,} from "@/service/familyTreeApi";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";
import {toast} from "react-toastify";

export const FamilyTreeRoleManagementModal = ({
	                                              showModal,
	                                              setShowModal,
	                                              targetMember,
	                                              familyTreeId,
	                                              treeAdmins,        // current admins list from parent
	                                              setTreeAdmins,     // update admins list in parent
	                                              onRoleChanged,     // callback to refresh tree role state
                                              }) => {
	const axiosPrivate = useAxiosPrivate();
	const {account} = useContext(AccountContext);
	const {treeRole} = useContext(NodeContext);

	const [loading, setLoading] = useState(false);

	if (!targetMember) return null;

	// Check if target is already an admin
	const targetAdmin = treeAdmins.find(
		(a) => a.accountId === targetMember.accountId
	);
	const targetRole = targetAdmin?.role || null;

	const canTransfer =
		treeRole === "SUPER_ADMIN" || account.primaryRole === "SUPER-USER";

	const handleAssignAdmin = async () => {
		setLoading(true);
		const res = await addAdmin(axiosPrivate, {
			familyTreeId,
			accountId: targetMember.accountId,
			role: "ADMIN",
		});
		if (res.success) {
			toast.success(res.message);
			setTreeAdmins(res.data);
			onRoleChanged?.();
			setShowModal(false);
		} else {
			toast.error(res.message || "Failed to assign admin role.");
		}
		setLoading(false);
	};

	const handleRevokeAdmin = async () => {
		setLoading(true);
		const res = await removeAdmin(axiosPrivate, {
			familyTreeId,
			accountId: targetMember.accountId,
		});
		if (res.success) {
			toast.success(res.message);
			setTreeAdmins(res.data);
			onRoleChanged?.();
			setShowModal(false);
		} else {
			toast.error(res.message || "Failed to revoke admin role.");
		}
		setLoading(false);
	};

	const handleTransferSuperAdmin = async () => {
		if (!window.confirm(
			`Are you sure you want to transfer Super-Admin to ${targetMember.firstName}? You will be demoted to Admin.`
		)) return;

		setLoading(true);

		if (targetAdmin) {
			// Already an admin — just update role
			const res = await updateAdminRole(axiosPrivate, {
				familyTreeId,
				accountId: targetMember.accountId,
				newRole: "SUPER_ADMIN",
			});
			if (res.success) {
				toast.success(res.message);
				setTreeAdmins(res.data);
				onRoleChanged?.();
				setShowModal(false);
			} else {
				toast.error(res.message || "Failed to transfer Super-Admin role.");
			}
		} else {
			// Not an admin yet — add as SUPER_ADMIN directly
			const res = await addAdmin(axiosPrivate, {
				familyTreeId,
				accountId: targetMember.accountId,
				role: "SUPER_ADMIN",
			});
			if (res.success) {
				toast.success(res.message);
				setTreeAdmins(res.data);
				onRoleChanged?.();
				setShowModal(false);
			} else {
				toast.error(res.message || "Failed to transfer Super-Admin role.");
			}
		}
		setLoading(false);
	};

	return (
		<Modal zIndex="z-[100]" isOpen={showModal}>
			<div className="flex flex-col gap-y-4 w-[25vw] bg-white rounded-xl p-[2vh]">
				{/* Header */}
				<div className="flex items-center justify-between">
					<h2 className="text-[1.25vw] font-custom-bold text-gray-900">
						{t("ROLE_MANAGEMENT.TITLE")}
					</h2>
					<button
						onClick={() => setShowModal(false)}
						className="w-[1.75vw] h-[1.75vw] flex items-center justify-center
                            bg-[#EAEAEA] rounded-md shadow-lg
                            hover:bg-[#F7F7F7] hover:ring hover:ring-[#D4D4D4]
                            transition duration-300 ease-out"
					>
						<img
							className="w-[75%] h-[75%]"
							src="/assets/close-icon.png"
							alt="Close"
						/>
					</button>
				</div>

				{/* Divider */}
				<div className="h-[0.2vh] w-full bg-[#ECECEC] rounded-full"/>

				{/* Member Info */}
				<div className="flex items-center gap-3 p-[1.5vh] rounded-xl border-2 border-[#EAEAEA]">
					{targetMember.imageUrl?.length <= 2 ? (
						<div
							className={`w-[5vh] h-[5vh] rounded-full flex items-center justify-center
                                text-white font-custom-mono-bold text-[1.5vh] ${targetMember.imageUrlBg}`}
						>
							{targetMember.imageUrl?.toUpperCase()}
						</div>
					) : (
						<img
							src={targetMember.imageUrl || "/assets/default-pfp.png"}
							className="w-[5vh] h-[5vh] rounded-full object-cover"
							referrerPolicy="no-referrer"
							alt="Member"
						/>
					)}
					<div className="flex-1">
						<p className="text-[1.1vw] font-custom-semi-bold text-gray-900">
							{targetMember.firstName} {targetMember.middleName}
						</p>
						<p className="text-[0.85vw] font-custom-medium text-gray-500">
							{targetRole
								? t(`ROLE_MANAGEMENT.CURRENT_ROLE.${targetRole}`)
								: t("ROLE_MANAGEMENT.CURRENT_ROLE.NONE")}
						</p>
					</div>
				</div>

				{/* Actions */}
				<div className="flex flex-col gap-2">
					{/* Assign / Revoke ADMIN */}
					{!targetRole && (
						<button
							disabled={loading}
							onClick={handleAssignAdmin}
							className="w-full flex items-center justify-center gap-2
                                px-[1vw] py-[1.25vh] rounded-lg
                                bg-emerald-500 hover:bg-emerald-600
                                disabled:opacity-50 disabled:cursor-not-allowed
                                text-white font-custom-medium text-[0.9vw]
                                transition-all duration-200"
						>
							{t("ROLE_MANAGEMENT.ACTIONS.ASSIGN_ADMIN")}
						</button>
					)}

					{targetRole === "ADMIN" && (
						<button
							disabled={loading}
							onClick={handleRevokeAdmin}
							className="w-full flex items-center justify-center gap-2
                                px-[1vw] py-[1.25vh] rounded-lg
                                bg-red-500 hover:bg-red-600
                                disabled:opacity-50 disabled:cursor-not-allowed
                                text-white font-custom-medium text-[0.9vw]
                                transition-all duration-200"
						>
							{t("ROLE_MANAGEMENT.ACTIONS.REVOKE_ADMIN")}
						</button>
					)}

					{/* Transfer SUPER_ADMIN — only for SUPER_ADMIN or PLATFORM SUPER-USER */}
					{canTransfer && targetRole !== "SUPER_ADMIN" && (
						<>
							<div className="h-[0.2vh] w-full bg-[#ECECEC] rounded-full my-1"/>
							<button
								disabled={loading}
								onClick={handleTransferSuperAdmin}
								className="w-full flex items-center justify-center gap-2
                                    px-[1vw] py-[1.25vh] rounded-lg
                                    bg-amber-500 hover:bg-amber-600
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    text-white font-custom-medium text-[0.9vw]
                                    transition-all duration-200"
							>
								{t("ROLE_MANAGEMENT.ACTIONS.TRANSFER_SUPER_ADMIN")}
							</button>
							<p className="text-[0.7vw] font-custom-regular text-gray-400 text-center">
								{t("ROLE_MANAGEMENT.ACTIONS.TRANSFER_WARNING")}
							</p>
						</>
					)}

					{/* Info for SUPER_ADMIN target */}
					{targetRole === "SUPER_ADMIN" && (
						<p className="text-[0.85vw] font-custom-medium text-amber-600 text-center py-2">
							{t("ROLE_MANAGEMENT.SUPER_ADMIN_INFO")}
						</p>
					)}
				</div>
			</div>
		</Modal>
	);
};